import { AlertTriangle, Info } from "lucide-react";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import type { ClassificationType } from "@/types/entity";
import type { ClassificationResult } from "@/utils/nis2-classifier";

const CLASSIFICATION_OPTIONS: {
  value: ClassificationType;
  label: string;
  tooltip: string;
}[] = [
  {
    value: "ESSENTIAL",
    label: "Entitate Esențială",
    tooltip: ONBOARDING_TOOLTIPS.clasificare_esentiala,
  },
  {
    value: "IMPORTANT",
    label: "Entitate Importantă",
    tooltip: ONBOARDING_TOOLTIPS.clasificare_importanta,
  },
  {
    value: "OUT_OF_SCOPE",
    label: "În afara domeniului",
    tooltip: ONBOARDING_TOOLTIPS.clasificare_out_of_scope,
  },
];

type ClassificationSectionProps = {
  classificationResult: ClassificationResult | null;
  manualClassification: ClassificationType | null;
  onClassificationChange: (classification: ClassificationType) => void;
  fieldErrors: Record<string, string>;
};

function ClassificationSection({
  classificationResult,
  manualClassification,
  onClassificationChange,
  fieldErrors,
}: ClassificationSectionProps) {
  const autoDetected = classificationResult?.classificationV2 as
    | ClassificationType
    | undefined;

  const isImpact = autoDetected === "IMPACT";

  const showMismatchWarning =
    manualClassification &&
    autoDetected &&
    !isImpact &&
    manualClassification !== autoDetected;

  return (
    <div className="py-4 space-y-6">
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium text-ds-text-600 mb-1">
            Selectează clasificarea entității
          </p>
          <p className="text-xs text-ds-text-500">
            Alege clasificarea NIS2 corespunzătoare entității tale
          </p>
        </div>

        <Select
          value={manualClassification ?? ""}
          onValueChange={(val) =>
            onClassificationChange(val as ClassificationType)
          }
        >
          <SelectTrigger
            className={cn(
              "w-full max-w-md",
              fieldErrors["manualClassification"] && "border-destructive"
            )}
          >
            <SelectValue placeholder="Selectează clasificarea..." />
          </SelectTrigger>
          <SelectContent>
            {CLASSIFICATION_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="space-y-1 mt-2">
          {CLASSIFICATION_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              className="flex items-center gap-1 text-xs text-ds-text-500"
            >
              <span className="font-medium">{opt.label}:</span>
              <FieldTooltip content={opt.tooltip} />
            </div>
          ))}
        </div>

        {fieldErrors["manualClassification"] && (
          <div className="flex items-center gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <span className="text-xs text-destructive">
              {fieldErrors["manualClassification"]}
            </span>
          </div>
        )}
      </div>

      {autoDetected && isImpact && (
        <Alert className="border-ds-amber-300 bg-ds-amber-50">
          <Info className="h-4 w-4 text-ds-amber-600" />
          <AlertDescription className="text-ds-amber-800 text-sm">
            Pentru a obține clasificarea NIS2, completează secțiunea{" "}
            <span className="font-semibold">Art. 9 - Evaluare Impact</span> de
            mai sus.
          </AlertDescription>
        </Alert>
      )}

      {autoDetected && !isImpact && (
        <div className="flex items-center gap-2 text-sm text-ds-text-500">
          <span>Clasificare detectată automat:</span>
          <ClassificationBadge classification={autoDetected} size="sm" />
        </div>
      )}

      {showMismatchWarning && (
        <Alert variant="destructive" className="border-ds-amber-300 bg-ds-amber-50">
          <AlertTriangle className="h-4 w-4 text-ds-amber-600" />
          <AlertDescription className="text-ds-amber-800 text-sm">
            Atenție: Clasificarea selectată diferă de cea detectată automat (
            <ClassificationBadge
              classification={autoDetected}
              size="sm"
              className="inline-flex mx-1"
            />
            ). Verificați dacă selecția este corectă.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export default ClassificationSection;
